/**
 * Client-side PDF download for audit reports.
 *
 * POSTs to /api/audit/pdf so nothing identifying lands in CDN/proxy/access
 * logs as a GET query param (P-1, docs/audits/2026-06-11-deep-audit.md).
 *
 * Authorization (S-015/S-017): the route accepts a signed link token, taken
 * from the report or success URL, or the caller's signed-in session. The
 * report owner's email used to be sent in the body and compared server-side,
 * which made the email the secret; it is no longer part of this request.
 */
export async function downloadReportPdf(reportId: string, opts: { token?: string } = {}): Promise<void> {
  const res = await fetch('/api/audit/pdf', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ reportId, token: opts.token }),
  });

  if (!res.ok) {
    let message = 'Failed to download PDF';
    try {
      const data = await res.json();
      message = data.error || message;
    } catch {
      // Non-JSON error body; keep the generic message
    }
    throw new Error(message);
  }

  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = objectUrl;

  const disposition = res.headers.get('Content-Disposition') || '';
  const match = disposition.match(/filename="([^"]+)"/);
  anchor.download = match?.[1] || `audit-report-${reportId.substring(0, 8)}.pdf`;

  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(objectUrl);
}
